import * as React from "react";
import { useMemo } from "react";
import { styled } from "@mui/material";
import { DataInputAddFindingsControlProps } from "../../dataInputAddFindingsControl";
import { Category } from "fe-common/graphql";
import { ScrollableContainer } from "fe-common/ui/commonStyles";
import { SingleViewPerLayoutBreakPoint } from "../../../util/aKaunGeneralUtil";
import { Breakpoint } from "@mui/material";
import { PatientCaseGrouping } from "../../../context/diagnosticAgent";
import { PanelsList } from "../editPatient/PanelsList";
import { useOrganizedPanels } from "./useOrganizedPanels";
import { useChangePatientCase } from "./useChangePatientCase";
//region [[ Styles ]]

const DataInputPanelAreaView = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  width: "100%",
  height: "100%",
});

const DataInputContent = styled("div")(({ theme }) => ({
  overflowY: "auto",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));

const PanelsListContainer = styled(ScrollableContainer)(({ theme }) => ({
  paddingBottom: "90px",
  paddingRight: theme.spacing(4),
  paddingLeft: theme.spacing(4),
  width: "100%",

  [theme.breakpoints.down(SingleViewPerLayoutBreakPoint)]: {
    paddingRight: theme.spacing(0),
    paddingLeft: theme.spacing(0),
  },
}));

// const TopSectionContainer = styled("div")({
//   zIndex: 3,
//   boxShadow: "#efefef 1px 5px 7px 0px",
//   position: "sticky",
// });
//endregion [[ Styles ]]

//region [[ Props ]]

export interface DataInputPanelAreaProps
  extends DataInputAddFindingsControlProps {
  placeholder?: string;
  showSearchArea?: boolean;
  className?: string;
  title?: string;
  width?: Breakpoint;
  categoryid?: string;
  panelId?: string;
  section?: PatientCaseGrouping;
  diagnose?: () => void;
  loading: boolean;
  showTabs?: boolean;
  onChangeSection?: (section: PatientCaseGrouping) => void;
  singlePanel?: boolean;
  sectionTitle?: string;
}

//endregion [[ Props ]]

//region [[ Functions ]]

//endregion [[ Functions ]]

export enum TabType {
  PANEL,
  DEMOGRAPHIC,
  CHIEF_COMPLAINT,
}

export type TabSelection = {
  tab: TabType;
  section: PatientCaseGrouping;
  subgroup?: Category;
  singlePanel?: boolean;
};

export const DataInputPanelArea = ({
  panels,
  currentCase,
  changeCase,
  placeholder,
  showSearchArea,
  title,
  width,
  categoryid,
  panelId,
  diagnose,
  section,
  loading,
  singlePanel,
  sectionTitle,
  ...props
}: DataInputPanelAreaProps) => {
  // -- organize panels into subgroups -
  const { panelsBySubGroup, initialSubgroup } = useOrganizedPanels(
    panels,
    categoryid,
    section,
  );

  const selectedTab = useMemo(() => {
    return {
      tab:
        section === PatientCaseGrouping.MAIN
          ? TabType.DEMOGRAPHIC
          : TabType.PANEL,
      section: section,
      subgroup: initialSubgroup,
      singlePanel: singlePanel,
    };
  }, [section, initialSubgroup, singlePanel]);

  const { setCaseValue, getCaseValue } = useChangePatientCase();

  const subgroups = useMemo(() => panelsBySubGroup.map((tli) => tli[0]), [
    panelsBySubGroup,
  ]);

  const panelsInSubGroup = useMemo(
    () => (c: Category) => ({
      loading: false,
      panels: panelsBySubGroup.find((tli) => tli[0] === c)?.[1],
    }),
    [panelsBySubGroup],
  );

  return (
    <DataInputPanelAreaView>
      <DataInputContent>
        <PanelsListContainer>
          <PanelsList
            subGroups={subgroups}
            panelsInSubGroup={panelsInSubGroup}
            selectedPanelId={panelId}
            loading={loading}
            currentCase={currentCase}
            selection={selectedTab} // initialSelected
            getCurrentResponse={getCaseValue}
            setResponse={setCaseValue}
            showTabs={props.showTabs}
            onChangeSection={props.onChangeSection}
            singlePanel={singlePanel}
            sectionTitle={sectionTitle}
          />
        </PanelsListContainer>
      </DataInputContent>
    </DataInputPanelAreaView>
  );
};
